import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import AICreateMode from "@/components/AICreateMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Edit, Trash2, FileText, ArrowLeft, Play, Clock, BookOpen, X, Wand2, MessageSquarePlus, Send, Lock } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import EntityContextMenu from "@/components/EntityContextMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChaptersPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const [campaign, setCampaign] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterSessions, setChapterSessions] = useState([]);
  const [formData, setFormData] = useState({ title: "", content: "" });
  const [createMode, setCreateMode] = useState("manual");
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);

  useEffect(() => { fetchData(); }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/chapters`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setChapters(chaptersRes.data);
    } catch {
      toast.error("Failed to load chapters");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        campaign_id: campaignId,
        order: editing ? editing.order : chapters.length
      };
      if (editing) {
        await axios.put(`${API}/chapters/${editing.id}`, payload, { withCredentials: true });
        toast.success("Chapter updated");
        if (selectedChapter?.id === editing.id) {
          setSelectedChapter({ ...selectedChapter, ...payload });
        }
      } else {
        await axios.post(`${API}/chapters`, payload, { withCredentials: true });
        toast.success("Chapter created");
      }
      setDialogOpen(false);
      setFormData({ title: "", content: "" });
      setEditing(null);
      fetchData();
    } catch {
      toast.error("Failed to save chapter");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Chapter", "Are you sure you want to delete this chapter?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/chapters/${id}`, { withCredentials: true });
      toast.success("Chapter deleted");
      if (selectedChapter?.id === id) setSelectedChapter(null);
      fetchData();
    } catch {
      toast.error("Failed to delete chapter");
    }
  };

  const fetchChapterSessions = async (chapterId) => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/chapters/${chapterId}/sessions`, { withCredentials: true });
      setChapterSessions(res.data);
    } catch {
      setChapterSessions([]);
    }
  };

  const selectChapter = (chapter) => {
    setSelectedChapter(chapter);
    fetchChapterSessions(chapter.id);
    setShowNoteInput(false);
    setNoteText("");
  };

  const addSessionNote = async () => {
    if (!noteText.trim() || !selectedChapter) return;
    setAddingNote(true);
    try {
      let sessionId;
      if (chapterSessions.length > 0) {
        sessionId = chapterSessions[0].id;
      } else {
        const res = await axios.post(`${API}/sessions`, {
          campaign_id: campaignId,
          title: `Session Notes: ${selectedChapter.title}`,
          chapter_id: selectedChapter.id
        }, { withCredentials: true });
        sessionId = res.data.id;
      }
      await axios.post(`${API}/sessions/${sessionId}/entries`, {
        type: "note",
        content: noteText
      }, { withCredentials: true });
      toast.success("Note added");
      setNoteText("");
      fetchChapterSessions(selectedChapter.id);
    } catch {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const openEdit = (chapter) => {
    setEditing(chapter);
    setFormData({ title: chapter.title, content: chapter.content || "" });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ title: "", content: "" });
    setCreateMode("manual");
    setDialogOpen(true);
  };

  const startSessionForChapter = () => {
    navigate(`/campaign/${campaignId}/sessions?chapter=${selectedChapter.id}&title=${encodeURIComponent(selectedChapter.title)}`);
  };

  /* Extract first ~150 chars of plain text from HTML content for preview */
  const getPreview = (html) => {
    if (!html) return "No content yet...";
    const text = html.replace(/<[^>]+>/g, "").trim();
    return text.length > 150 ? text.slice(0, 150) + "..." : text;
  };

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="chapters-page">
        {/* ==================== HEADER ==================== */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            {selectedChapter && (
              <button onClick={() => setSelectedChapter(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors" data-testid="back-to-chapters">
                <ArrowLeft className="w-3 h-3" /> Back to Chapters
              </button>
            )}
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">
              {selectedChapter ? selectedChapter.title : "Campaign Story"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {selectedChapter ? `Chapter ${chapters.findIndex(c => c.id === selectedChapter.id) + 1} of ${chapters.length}` : `${chapters.length} chapter${chapters.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          {!selectedChapter && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="create-chapter-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  <Plus className="w-4 h-4 mr-2" /> New Chapter
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="font-serif text-2xl">{editing ? "Edit Chapter" : "New Chapter"}</DialogTitle>
                </DialogHeader>
                {!editing && (
                  <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
                    <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                      <Plus className="w-3 h-3 mr-1" /> Write Manually
                    </Button>
                    {!isCommoner && (
                      <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                        <Wand2 className="w-3 h-3 mr-1" /> Use AI
                      </Button>
                    )}
                    {isCommoner && (
                      <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                        <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                      </Button>
                    )}
                  </div>
                )}
                {!editing && createMode === "ai" && (
                  <AICreateMode entityType="chapter" campaignId={campaignId} onGenerated={(data) => {
                    setFormData(prev => ({ ...prev, ...data }));
                    setCreateMode("manual");
                  }} />
                )}
                <form onSubmit={handleSubmit} className="space-y-4 mt-2 overflow-y-auto flex-1 pr-1" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Title</label>
                    <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Chapter I: The Call to Adventure" required data-testid="chapter-title-input" className="bg-input border-border rounded-sm" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Story Content</label>
                    <RichTextEditor value={formData.content} onChange={(v) => setFormData({ ...formData, content: v })} placeholder="Write the story of this chapter..." />
                  </div>
                  <Button type="submit" className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-chapter-button">
                    {editing ? "Save Changes" : "Create Chapter"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {selectedChapter && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm border-border" onClick={() => openEdit(selectedChapter)} data-testid="edit-chapter-button">
                <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <VisibilityToggle entityType="chapters" entityId={selectedChapter.id} currentVisibility={selectedChapter.visibility || "dm_only"} onUpdate={() => fetchData()} />
              <Button variant="outline" size="sm" className="rounded-sm border-border" onClick={startSessionForChapter} data-testid="start-session-button">
                <Play className="w-3.5 h-3.5 mr-1.5" /> Start Session
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedChapter.id)} data-testid="delete-chapter-button">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* ==================== CHAPTER GRID (when no chapter selected) ==================== */}
        {!selectedChapter && (
          <div>
            {chapters.length === 0 ? (
              <div className="empty-state">
                <BookOpen className="empty-state-icon text-muted-foreground" />
                <h3 className="text-xl font-serif text-foreground mb-2">No chapters yet</h3>
                <p className="text-muted-foreground mb-6">Begin writing your campaign story</p>
                <Button onClick={openCreate} className="bg-primary text-primary-foreground rounded-sm">
                  <Plus className="w-4 h-4 mr-2" /> Write First Chapter
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="chapters-grid">
                {chapters.map((chapter, idx) => (
                  <EntityContextMenu
                    key={chapter.id}
                    onOpen={() => selectChapter(chapter)}
                    onRename={() => openEdit(chapter)}
                    onDelete={() => handleDelete(chapter.id)}
                    pinPath="chapters"
                    pinLabel={`Chapter: ${chapter.title}`}
                    entityName={chapter.title}
                  >
                  <Card
                    className="bg-card border-border rounded-sm cursor-pointer entity-card hover:border-primary/30 transition-all group"
                    onClick={() => selectChapter(chapter)}
                    data-testid={`chapter-card-${chapter.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-sm">
                            Ch. {idx + 1}
                          </span>
                          <VisibilityToggle entityType="chapters" entityId={chapter.id} currentVisibility={chapter.visibility || "dm_only"} onUpdate={() => fetchData()} />
                        </div>
                        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                          <button className="p-1 hover:text-primary transition-colors" onClick={() => openEdit(chapter)} data-testid={`edit-chapter-${chapter.id}`}>
                            <Edit className="w-3 h-3" />
                          </button>
                          <button className="p-1 hover:text-destructive transition-colors" onClick={() => handleDelete(chapter.id)} data-testid={`delete-chapter-${chapter.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <h3 className="text-base font-serif text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {chapter.title}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                        {getPreview(chapter.content)}
                      </p>
                      <div className="flex items-center gap-1 mt-3 text-[10px] text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {chapter.created_at ? new Date(chapter.created_at).toLocaleDateString() : ""}
                      </div>
                    </CardContent>
                  </Card>
                  </EntityContextMenu>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== FULL READING VIEW (when chapter selected) ==================== */}
        {selectedChapter && (
          <div data-testid="chapter-reading-view">
            {/* Story content */}
            <Card className="bg-card border-border rounded-sm mb-6">
              <CardContent className="p-8 md:p-12 max-w-3xl mx-auto">
                {selectedChapter.content ? (
                  <div className="rich-content prose prose-invert max-w-none prose-p:leading-relaxed prose-p:mb-4 prose-headings:font-serif">
                    <RichTextDisplay content={selectedChapter.content} />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                    <p className="text-muted-foreground">This chapter has no content yet.</p>
                    <Button variant="outline" size="sm" className="mt-3 rounded-sm border-border" onClick={() => openEdit(selectedChapter)}>
                      <Edit className="w-3.5 h-3.5 mr-1" /> Start Writing
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inline Session Notes */}
            <div className="mb-6" data-testid="inline-session-notes">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <MessageSquarePlus className="w-3.5 h-3.5" /> Session Notes
                  {chapterSessions.length > 0 && (
                    <span className="text-xs text-muted-foreground/60">
                      ({chapterSessions.reduce((sum, s) => sum + (s.entries?.length || 0), 0)} notes)
                    </span>
                  )}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm border-border text-xs h-7"
                  onClick={() => setShowNoteInput(!showNoteInput)}
                  data-testid="toggle-add-note"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Note
                </Button>
              </div>

              {/* Add note input */}
              {showNoteInput && (
                <div className="mb-4 flex gap-2" data-testid="note-input-section">
                  <Input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a session note for this chapter..."
                    className="bg-input border-border rounded-sm flex-1"
                    onKeyDown={(e) => e.key === "Enter" && !addingNote && noteText.trim() && addSessionNote()}
                    disabled={addingNote}
                    data-testid="note-text-input"
                  />
                  <Button
                    onClick={addSessionNote}
                    disabled={addingNote || !noteText.trim()}
                    className="bg-primary text-primary-foreground rounded-sm"
                    data-testid="submit-note"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}

              {/* Existing notes */}
              {chapterSessions.length > 0 && (
                <div className="space-y-2">
                  {chapterSessions.map(session => (
                    session.entries?.map((entry, idx) => (
                      <div key={`${session.id}-${idx}`} className="flex gap-3 p-3 rounded-sm bg-muted/10 border border-border/30" data-testid={`session-note-${idx}`}>
                        <div className="w-1 bg-primary/30 rounded-full flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">{entry.content}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}
                          </p>
                        </div>
                      </div>
                    ))
                  ))}
                </div>
              )}
            </div>

            {/* Linked Sessions */}
            {chapterSessions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Linked Sessions ({chapterSessions.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {chapterSessions.map(session => (
                    <Card key={session.id} className="bg-card border-border rounded-sm cursor-pointer hover:border-primary/30" onClick={() => navigate(`/campaign/${campaignId}/sessions`)} data-testid={`linked-session-${session.id}`}>
                      <CardContent className="p-3 flex items-center gap-3">
                        <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{session.title || `Session ${session.session_number}`}</p>
                          <p className="text-xs text-muted-foreground">{session.date ? new Date(session.date).toLocaleDateString() : ""}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Chapter Navigation */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
              {(() => {
                const idx = chapters.findIndex(c => c.id === selectedChapter.id);
                const prev = idx > 0 ? chapters[idx - 1] : null;
                const next = idx < chapters.length - 1 ? chapters[idx + 1] : null;
                return (
                  <>
                    {prev ? (
                      <button className="text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => selectChapter(prev)} data-testid="prev-chapter">
                        <ArrowLeft className="w-3 h-3 inline mr-1" /> {prev.title}
                      </button>
                    ) : <div />}
                    {next ? (
                      <button className="text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => selectChapter(next)} data-testid="next-chapter">
                        {next.title} <ArrowLeft className="w-3 h-3 inline ml-1 rotate-180" />
                      </button>
                    ) : <div />}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Edit Dialog (reused when editing from reading view) */}
        {editing && !dialogOpen && null}
        <Dialog open={dialogOpen && !!editing} onOpenChange={(v) => { if (!v) { setEditing(null); } setDialogOpen(v); }}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="font-serif text-2xl">Edit Chapter</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2 overflow-y-auto flex-1 pr-1">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Title</label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Chapter Title" required className="bg-input border-border rounded-sm" data-testid="chapter-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Story Content</label>
                <RichTextEditor value={formData.content} onChange={(v) => setFormData({ ...formData, content: v })} placeholder="Write the story..." />
              </div>
              <Button type="submit" className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-chapter-button">
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ChaptersPage;
